// src/pages/Certifications.tsx
import React from "react";
import AppShell from "../layout/AppShell";
import {
  listCertificationsPage,
  listCertificationReports,
  upsertCertification,
  deleteCertification,
} from "../services/certificationsService";
import type {
  Certification,
  CertificationType,
  CertificationStatus,
  CertificationReportFile,
  ListCertsPage,
  UpsertPatch,
} from "../models/certification";
import {
  MdWorkspacePremium,
  MdRefresh,
  MdDownload,
  MdDelete,
  MdEdit,
  MdAdd,
  MdAttachment,
  MdClose,
  MdSave,
  MdContentCopy,
  MdSearch,
} from "react-icons/md";
import {
  Timestamp,
  getFirestore,
  collection,
  getDocs,
  where,
  query as fsQuery,
  documentId,
} from "firebase/firestore";
import { FirebaseError } from "firebase/app";
import { useAuthStore } from "../stores/authStore";
import { CERTIFICATIONS_ROLES } from "../models/auth";

/* ───────────────────── permisos ───────────────────── */
const useCan = () => ({
  canEdit: useAuthStore((s) => s.hasAnyRole(CERTIFICATIONS_ROLES)),
  canSeeReports: useAuthStore((s) => s.hasAnyRole(CERTIFICATIONS_ROLES)),
  me: useAuthStore((s) => s.state.user?.uid ?? null),
});

/* ───────── helpers ───────── */
type UserBrief = { fullName?: string; email?: string };

function chunk<T>(arr: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/* ── filtros base ── */
const TYPE_OPTIONS = [
  { value: "official", label: "Oficiales" },
  { value: "trial", label: "De prueba" },
  { value: "all", label: "Todas" },
] as const;

const STATUS_OPTIONS = [
  "draft",
  "final",
] as const satisfies readonly CertificationStatus[];

/* ── SearchField ── */
type SearchFieldProps = {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  title?: string;
  className?: string;
};
const SearchField: React.FC<SearchFieldProps> = React.memo(
  ({ value, onChange, placeholder, title, className = "" }) => {
    return (
      <div className={`relative w-full ${className}`} title={title}>
        <MdSearch
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-on-surface/60"
          aria-hidden
        />
        <input
          type="text"
          autoComplete="off"
          spellCheck={false}
          className="input-surface w-full leading-[1.2]"
          style={{ paddingLeft: 36 }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      </div>
    );
  }
);

/* ───────── componente ───────── */
export default function Certifications(): React.ReactElement {
  const [rows, setRows] = React.useState<Certification[]>([]);
  const [cursor, setCursor] = React.useState<ListCertsPage["next"]>(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const { canEdit, canSeeReports, me } = useCan();

  // filtros
  const [typeFilter, setTypeFilter] = React.useState<
    "official" | "trial" | "all"
  >("official");
  const [statusFilter, setStatusFilter] = React.useState<
    "" | CertificationStatus
  >("");
  const [userQuery, setUserQuery] = React.useState("");
  const [entityQuery, setEntityQuery] = React.useState("");
  const [freeQuery, setFreeQuery] = React.useState("");

  // cache de usuarios
  const [owners, setOwners] = React.useState<Record<string, UserBrief>>({});

  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Certification | null>(null);

  const [reportsOpen, setReportsOpen] = React.useState<string | null>(null);
  const [reports, setReports] = React.useState<
    Record<string, CertificationReportFile[]>
  >({});

  const loadOwners = React.useCallback(
    async (uids: string[]) => {
      const missing = uids.filter((u) => !owners[u]);
      if (missing.length === 0) return;

      const db = getFirestore();
      const col = collection(db, "users");

      const found: Record<string, UserBrief> = {};
      for (const ids of chunk(missing, 10)) {
        const snap = await getDocs(
          fsQuery(col, where(documentId(), "in", ids))
        );
        snap.forEach((doc) => {
          const d = doc.data() as any;
          found[doc.id] = {
            fullName: typeof d.fullName === "string" ? d.fullName : undefined,
            email: typeof d.email === "string" ? d.email : undefined,
          };
        });
      }
      setOwners((prev) => ({ ...prev, ...found }));
    },
    [owners]
  );

  const load = React.useCallback(
    async (reset = false) => {
      setLoading(true);
      try {
        const page = await listCertificationsPage({
          type: typeFilter,
          limit: 20,
          cursor: reset ? null : cursor,
        });
        const nextRows = reset ? page.items : [...rows, ...page.items];
        setRows(nextRows);
        setCursor(page.next);
        setErr(null);

        const uids = Array.from(new Set(nextRows.map((c) => c.ownerUid)));
        void loadOwners(uids);
      } catch (e) {
        const fe = e as FirebaseError;
        setErr(
          fe.code === "permission-denied"
            ? "No tienes permisos para ver estas certificaciones."
            : fe.message
        );
      } finally {
        setLoading(false);
      }
    },
    [typeFilter, cursor, rows, loadOwners]
  );

  React.useEffect(() => {
    void load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter]);

  // aplica filtros en memoria
  const filtered = React.useMemo(() => {
    const uq = userQuery.trim().toLowerCase();
    const eq = entityQuery.trim().toLowerCase();
    const fq = freeQuery.trim().toLowerCase();

    return rows.filter((c) => {
      const u = owners[c.ownerUid];
      const email = (u?.email ?? "").toLowerCase();
      const name = (u?.fullName ?? "").toLowerCase();
      const entNom = (c.entity?.nombre ?? "").toLowerCase();
      const entTipo = (c.entity?.tipo ?? "").toLowerCase();

      const passStatus = statusFilter ? c.status === statusFilter : true;
      const passUser = uq === "" || email.includes(uq) || name.includes(uq);
      const passEntity =
        eq === "" || entNom.includes(eq) || entTipo.includes(eq);

      const pool = [
        c.id,
        c.versionId ?? "",
        c.status,
        c.type,
        entNom,
        entTipo,
        name,
        email,
      ]
        .filter(Boolean)
        .map((s) => String(s).toLowerCase());

      const passFree = fq === "" || pool.some((s) => s.includes(fq));
      return passStatus && passUser && passEntity && passFree;
    });
  }, [rows, owners, statusFilter, userQuery, entityQuery, freeQuery]);

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const openEdit = (c: Certification) => {
    setEditing(c);
    setDrawerOpen(true);
  };

  const userCanSeeReportsFor = (c: Certification) =>
    canSeeReports || (me !== null && me === c.ownerUid);

  const toggleReports = async (cert: Certification) => {
    if (!userCanSeeReportsFor(cert)) return;
    const key = cert.id;
    if (reportsOpen === key) {
      setReportsOpen(null);
      return;
    }
    if (!reports[key]) {
      const list = await listCertificationReports({
        ownerUid: cert.ownerUid,
        evaluationId: cert.id,
      });
      setReports((prev) => ({ ...prev, [key]: list }));
    }
    setReportsOpen(key);
  };

  const onDelete = async (c: Certification) => {
    const ok = window.confirm(
      "¿Eliminar certificación? Esta acción no se puede deshacer."
    );
    if (!ok) return;
    await deleteCertification(c.ownerUid, c.type, c.id);
    setRows((r) =>
      r.filter((x) => !(x.id === c.id && x.ownerUid === c.ownerUid))
    );
  };

  return (
    <AppShell>
      <section className="card p-4">
        {/* Header + filtros */}
        <div className="mb-4 grid grid-cols-1 gap-2 md:grid-cols-[auto,1fr] md:items-center">
          <div className="flex items-center gap-2">
            <span className="icon-accent text-2xl">
              <MdWorkspacePremium />
            </span>
            <h2 className="text-lg font-semibold">Certificaciones</h2>
            <span className="badge-tonal ml-2">{filtered.length} visibles</span>
          </div>

          <div className="flex w-full items-center gap-2 overflow-x-auto pb-1 [scrollbar-width:thin]">
            <select
              className="input-surface grow basis-40 min-w-[10rem]"
              value={typeFilter}
              onChange={(e) =>
                setTypeFilter(e.target.value as "official" | "trial" | "all")
              }
              title="Tipo"
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>

            <select
              className="input-surface grow basis-40 min-w-[10rem]"
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(
                  (e.target.value || "") as "" | CertificationStatus
                )
              }
              title="Estado"
            >
              <option value="">Todos los estados</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <SearchField
              value={userQuery}
              onChange={setUserQuery}
              placeholder="Usuario (email o nombre)…"
              title="Usuario"
              className="grow basis-64 min-w-[16rem]"
            />
            <SearchField
              value={entityQuery}
              onChange={setEntityQuery}
              placeholder="Entidad (nombre o tipo)…"
              title="Entidad"
              className="grow basis-64 min-w-[16rem]"
            />
            <SearchField
              value={freeQuery}
              onChange={setFreeQuery}
              placeholder="Buscar por id / versión / estado…"
              title="Buscar"
              className="grow basis-[20rem] min-w-[18rem]"
            />

            <button
              className="btn btn-outline flex items-center gap-1 shrink-0 whitespace-nowrap"
              onClick={() => load(true)}
              disabled={loading}
              type="button"
              title="Recargar"
            >
              <MdRefresh /> Recargar
            </button>

            {canEdit && (
              <button
                className="btn btn-primary flex items-center gap-1 shrink-0 whitespace-nowrap"
                onClick={openCreate}
                type="button"
                title="Nueva certificación"
              >
                <MdAdd /> Nueva
              </button>
            )}
          </div>
        </div>

        {err && <p className="mb-3 text-sm text-red-600">{err}</p>}

        {/* Tabla escritorio */}
        <div className="hidden overflow-x-auto rounded-lg border border-surface md:block">
          <table className="table-tonal text-sm">
            <thead>
              <tr>
                <Th>Tipo</Th>
                <Th>Estado</Th>
                <Th>Entidad</Th>
                <Th>Versión</Th>
                <Th>Usuario</Th>
                <Th>Actualizado</Th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <>
                  <SkeletonRow />
                </>
              ) : (
                filtered.map((c) => {
                  const last =
                    c.updatedAt?.toDate?.() ??
                    c.createdAt?.toDate?.() ??
                    undefined;
                  const u = owners[c.ownerUid];
                  const canSeeThis = userCanSeeReportsFor(c);
                  return (
                    <React.Fragment key={`${c.ownerUid}_${c.id}`}>
                      <tr>
                        <Td>
                          <span className="badge-tonal">
                            {c.type === "official" ? "Oficial" : "Prueba"}
                          </span>
                        </Td>
                        <Td>
                          <StatusBadge status={c.status} />
                        </Td>
                        <Td className="max-w-[360px]">
                          {c.entity?.nombre || "—"}
                          {c.entity?.tipo ? (
                            <span className="ml-2 text-xs opacity-70">
                              ({c.entity.tipo})
                            </span>
                          ) : null}
                        </Td>
                        <Td>{c.versionId ?? "—"}</Td>
                        <Td className="max-w-[320px]">
                          <div className="truncate">{u?.fullName ?? "—"}</div>
                          <div className="truncate text-xs opacity-70">
                            {u?.email ? (
                              u.email
                            ) : (
                              <code>{c.ownerUid.slice(0, 8)}…</code>
                            )}
                          </div>
                        </Td>
                        <Td>{last ? last.toLocaleString() : "—"}</Td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex justify-end gap-2">
                            {canSeeThis && (
                              <button
                                className="btn btn-outline flex items-center gap-1"
                                onClick={() => toggleReports(c)}
                                type="button"
                                title="Ver reportes"
                              >
                                <MdAttachment /> Reportes
                              </button>
                            )}
                            {canEdit && (
                              <>
                                <button
                                  className="btn btn-outline flex items-center gap-1"
                                  onClick={() => openEdit(c)}
                                  type="button"
                                >
                                  <MdEdit /> Editar
                                </button>
                                <button
                                  className="btn btn-outline flex items-center gap-1"
                                  onClick={() => onDelete(c)}
                                  type="button"
                                >
                                  <MdDelete /> Eliminar
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>

                      {reportsOpen === c.id && (
                        <tr>
                          <td colSpan={7} className="px-3 pb-3">
                            <ReportsPanel
                              evaluationId={c.id}
                              files={reports[c.id] ?? []}
                              onReload={async () => {
                                const list = await listCertificationReports({
                                  ownerUid: c.ownerUid,
                                  evaluationId: c.id,
                                });
                                setReports((prev) => ({
                                  ...prev,
                                  [c.id]: list,
                                }));
                              }}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="py-10 text-center text-on-surface/70"
                  >
                    Sin resultados para los filtros.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Lista móvil */}
        <div className="space-y-2 md:hidden">
          {loading && rows.length === 0 ? (
            <MobileSkeleton />
          ) : (
            filtered.map((c) => {
              const last =
                c.updatedAt?.toDate?.() ?? c.createdAt?.toDate?.() ?? undefined;
              const u = owners[c.ownerUid];
              const canSeeThis = userCanSeeReportsFor(c);
              return (
                <article
                  key={`${c.ownerUid}_${c.id}`}
                  className="rounded-xl border border-surface bg-surface p-3"
                >
                  <div className="mb-1 flex items-center justify-between">
                    <span className="badge-tonal">
                      {c.type === "official" ? "Oficial" : "Prueba"}
                    </span>
                    <StatusBadge status={c.status} />
                  </div>
                  <div className="text-sm font-medium">
                    {c.entity?.nombre ?? "—"}{" "}
                    {c.entity?.tipo ? (
                      <span className="ml-2 text-xs opacity-70">
                        ({c.entity.tipo})
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-1 text-xs opacity-70">
                    {u?.fullName ? u.fullName : "—"}{" "}
                    {u?.email ? `• ${u.email}` : `• ${c.ownerUid.slice(0, 8)}…`}
                  </div>
                  <div className="mt-1 text-xs opacity-70">
                    {c.versionId ? `v: ${c.versionId} • ` : ""}
                    {last ? `Act: ${last.toLocaleString()}` : ""}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {canSeeThis && (
                      <button
                        className="btn btn-outline flex items-center gap-1"
                        onClick={() => toggleReports(c)}
                        type="button"
                      >
                        <MdAttachment /> Reportes
                      </button>
                    )}
                    {canEdit && (
                      <>
                        <button
                          className="btn btn-outline flex items-center gap-1"
                          onClick={() => openEdit(c)}
                          type="button"
                        >
                          <MdEdit /> Editar
                        </button>
                        <button
                          className="btn btn-outline flex items-center gap-1"
                          onClick={() => onDelete(c)}
                          type="button"
                        >
                          <MdDelete /> Eliminar
                        </button>
                      </>
                    )}
                  </div>
                </article>
              );
            })
          )}
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button
            className="btn btn-outline"
            onClick={() => load(false)}
            disabled={loading || !cursor}
            type="button"
          >
            {loading ? "Cargando…" : cursor ? "Cargar más" : "No hay más"}
          </button>
        </div>
      </section>

      <CertDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        editing={editing}
        onSaved={(saved) => {
          void loadOwners([saved.ownerUid]);
          if (!editing) setRows((r) => [saved, ...r]);
          else
            setRows((r) =>
              r.map((x) =>
                x.id === saved.id && x.ownerUid === saved.ownerUid ? saved : x
              )
            );
        }}
      />
    </AppShell>
  );
}

/* helpers UI */
function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2">{children}</th>;
}
function Td({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>;
}

function StatusBadge({ status }: { status: CertificationStatus }) {
  const map: Record<string, string> = {
    draft: "color-mix(in oklab, var(--color-onSurface) 12%, transparent)",
    final: "color-mix(in oklab, var(--color-primary) 22%, transparent)",
  };
  return (
    <span
      className="badge-tonal"
      style={{ background: map[status], borderColor: "transparent" }}
      title={`Estado: ${status}`}
    >
      {status}
    </span>
  );
}

function SkeletonRow() {
  return (
    <tr>
      {[...Array(6)].map((_, i) => (
        <td key={i} className="px-3 py-3">
          <div className="h-4 w-full max-w-[220px] rounded bg-black/10 dark:bg-white/10" />
        </td>
      ))}
      <td className="px-3 py-3 text-right">
        <div className="ml-auto h-8 w-24 rounded bg-black/10 dark:bg-white/10" />
      </td>
    </tr>
  );
}

function MobileSkeleton() {
  return (
    <div className="rounded-xl border border-surface bg-surface p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="inline-block h-5 w-12 rounded-full bg-black/10 dark:bg-white/10" />
        <span className="inline-block h-6 w-24 rounded bg-black/10 dark:bg-white/10" />
      </div>
      <div className="h-4 w-5/6 rounded bg-black/10 dark:bg-white/10" />
      <div className="mt-2 h-4 w-2/3 rounded bg-black/10 dark:bg-white/10" />
    </div>
  );
}

/* Drawer (igual que tu versión) */
function ReportsPanel({
  evaluationId,
  files,
  onReload,
}: {
  evaluationId: string;
  files: CertificationReportFile[];
  onReload: () => Promise<void>;
}) {
  return (
    <div className="rounded-xl border border-surface bg-surface p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-semibold">
          Reportes — <code>{evaluationId.slice(0, 8)}…</code>
        </div>
        <button
          className="btn btn-outline flex items-center gap-1"
          onClick={onReload}
        >
          <MdRefresh /> Recargar
        </button>
      </div>
      {files.length === 0 ? (
        <div className="text-sm text-on-surface/70">No hay archivos.</div>
      ) : (
        <ul className="space-y-2">
          {files.map((f) => (
            <li
              key={f.path}
              className="flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="truncate text-sm">{f.name}</div>
                <div className="text-xs opacity-70">
                  {f.updated ? f.updated.toLocaleString() : ""}{" "}
                  {typeof f.size === "number"
                    ? `• ${(f.size / (1024 * 1024)).toFixed(2)} MB`
                    : ""}
                </div>
              </div>
              <div className="shrink-0">
                <a
                  className="btn btn-outline inline-flex items-center gap-1"
                  href={f.downloadURL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MdDownload /> Descargar
                </a>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* Drawer de edición/creación (igual que tu versión) */
function CertDrawer({
  open,
  onClose,
  editing,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  editing: Certification | null;
  onSaved: (saved: Certification) => void;
}): React.ReactElement | null {
  const [ownerUid, setOwnerUid] = React.useState("");
  const [id, setId] = React.useState("");
  const [type, setType] = React.useState<CertificationType>("official");
  const [status, setStatus] = React.useState<CertificationStatus>("draft");
  const [nombre, setNombre] = React.useState("");
  const [tipoEntidad, setTipoEntidad] = React.useState("");
  const [versionId, setVersionId] = React.useState("");

  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    if (editing) {
      setOwnerUid(editing.ownerUid);
      setId(editing.id);
      setType(editing.type);
      setStatus(editing.status);
      setNombre(editing.entity?.nombre ?? "");
      setTipoEntidad(editing.entity?.tipo ?? "");
      setVersionId(editing.versionId ?? "");
    } else {
      setOwnerUid("");
      setId("");
      setType("official");
      setStatus("draft");
      setNombre("");
      setTipoEntidad("");
      setVersionId("");
    }
    setError(null);
  }, [open, editing]);

  if (!open) return null;

  const close = () => {
    if (!saving) onClose();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ownerUid.trim() || !id.trim()) {
      setError("UID de usuario e ID de certificación son requeridos.");
      return;
    }
    try {
      setSaving(true);

      const patch: UpsertPatch = buildPatch({
        status,
        versionId: versionId.trim(),
        nombre: nombre.trim(),
        tipoEntidad: tipoEntidad.trim(),
        isCreate: !editing,
      });

      await upsertCertification({
        ownerUid: ownerUid.trim(),
        type,
        id: id.trim(),
        patch,
      });

      const saved = buildSaved({
        id: id.trim(),
        ownerUid: ownerUid.trim(),
        type,
        status,
        versionId: versionId.trim(),
        nombre: nombre.trim(),
        tipoEntidad: tipoEntidad.trim(),
        ...(editing?.createdAt ? { createdAt: editing.createdAt } : {}),
      });

      onSaved(saved);
      onClose();
    } catch (e2) {
      setError((e2 as Error).message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/40" onClick={close} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-xl overflow-y-auto bg-surface p-4 shadow-xl">
        <form onSubmit={submit} className="flex h-full flex-col">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {editing ? "Editar certificación" : "Nueva certificación"}
            </h3>
            <button
              type="button"
              className="btn btn-outline"
              onClick={close}
              disabled={saving}
              aria-label="Cerrar"
            >
              <MdClose />
            </button>
          </div>

          <div className="grow space-y-3">
            {!editing && (
              <>
                <Field label="UID del usuario *">
                  <div className="relative">
                    <input
                      className="input-surface pr-8"
                      value={ownerUid}
                      onChange={(e) => setOwnerUid(e.currentTarget.value)}
                      placeholder="g0wEjEaC0KZ7TgEB3bOnc7YJfY32"
                    />
                    <button
                      type="button"
                      className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-2 text-on-surface/70 hover:bg-black/5 dark:hover:bg-white/10"
                      onClick={() => navigator.clipboard.writeText(ownerUid)}
                      title="Copiar UID"
                    >
                      <MdContentCopy />
                    </button>
                  </div>
                </Field>

                <Field label="ID de certificación *">
                  <input
                    className="input-surface"
                    value={id}
                    onChange={(e) => setId(e.currentTarget.value)}
                    placeholder="rxDGMIMHigWEV0jM021y"
                  />
                </Field>
              </>
            )}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Tipo *">
                <select
                  className="input-surface"
                  value={type}
                  onChange={(e) => setType(e.target.value as CertificationType)}
                >
                  <option value="official">Oficial</option>
                  <option value="trial">Prueba</option>
                </select>
              </Field>

              <Field label="Estado *">
                <select
                  className="input-surface"
                  value={status}
                  onChange={(e) =>
                    setStatus(e.target.value as CertificationStatus)
                  }
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Entidad (nombre)">
              <input
                className="input-surface"
                value={nombre}
                onChange={(e) => setNombre(e.currentTarget.value)}
                placeholder="Compañía XYZ"
              />
            </Field>

            <Field label="Entidad (tipo)">
              <input
                className="input-surface"
                value={tipoEntidad}
                onChange={(e) => setTipoEntidad(e.currentTarget.value)}
                placeholder="Empresa / ONG / etc."
              />
            </Field>

            <Field label="Versión">
              <input
                className="input-surface"
                value={versionId}
                onChange={(e) => setVersionId(e.currentTarget.value)}
                placeholder="v1-doc"
              />
            </Field>

            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>

          <div className="sticky bottom-0 mt-3 flex items-center gap-2 border-t border-surface bg-surface py-2">
            <button
              type="submit"
              className="btn btn-primary flex items-center gap-1"
              disabled={saving}
            >
              <MdSave /> {saving ? "Guardando…" : "Guardar"}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={close}
              disabled={saving}
            >
              <MdClose /> Cancelar
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}

/* helpers Drawer */
function buildPatch(input: {
  status: CertificationStatus;
  versionId: string;
  nombre: string;
  tipoEntidad: string;
  isCreate: boolean;
}): UpsertPatch {
  const now = Timestamp.now();
  const patch: UpsertPatch = { status: input.status, updatedAt: now };
  if (input.isCreate) patch.createdAt = now;

  const ent: { nombre?: string; tipo?: string } = {};
  if (input.nombre) ent.nombre = input.nombre;
  if (input.tipoEntidad) ent.tipo = input.tipoEntidad;
  if (Object.keys(ent).length) patch.entity = ent;

  if (input.versionId) patch.versionId = input.versionId;
  return patch;
}

function buildSaved(input: {
  id: string;
  ownerUid: string;
  type: CertificationType;
  status: CertificationStatus;
  versionId: string;
  nombre: string;
  tipoEntidad: string;
  createdAt?: Timestamp;
}): Certification {
  const now = Timestamp.now();
  const saved: Certification = {
    id: input.id,
    ownerUid: input.ownerUid,
    type: input.type,
    status: input.status,
    updatedAt: now,
    createdAt: input.createdAt ?? now,
  };

  if (input.versionId) saved.versionId = input.versionId;
  if (input.nombre || input.tipoEntidad) {
    saved.entity = {
      ...(input.nombre ? { nombre: input.nombre } : {}),
      ...(input.tipoEntidad ? { tipo: input.tipoEntidad } : {}),
    };
  }
  return saved;
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className="block text-xs text-on-surface/70">{label}</label>
      <div>{children}</div>
    </div>
  );
}
